import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FileText, Clock, MapPin, DollarSign, Package, ChevronLeft, ChevronRight, AlertCircle, Send, Eye } from 'lucide-react';
import { rfqsApi, categoriesApi, countriesApi } from '../services/api';
import { useAuth } from '../context/AuthContext';

const RFQsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);

  const page = parseInt(searchParams.get('page') || '1');
  const category = searchParams.get('category') || '';
  const country = searchParams.get('country') || '';
  const urgent = searchParams.get('urgent') || '';

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const params = { page, limit: 12 };
        if (category) params.category = category;
        if (country) params.country = country;
        if (urgent) params.urgent = urgent === 'true';

        const [rfqsRes, categoriesRes, countriesRes] = await Promise.all([
          rfqsApi.getAll(params),
          categoriesApi.getAll(),
          countriesApi.getAll()
        ]);
        setRfqs(rfqsRes.data.rfqs || []);
        setTotalPages(rfqsRes.data.pages || 1);
        setTotal(rfqsRes.data.total || 0);
        setCategories(categoriesRes.data || []);
        setCountries(countriesRes.data || []);
      } catch (error) {
        console.error('Error fetching RFQs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [page, category, country, urgent]);

  const updateFilter = (key, value) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <Header />

      {/* Hero */}
      <section className="bg-[#0A192F] py-12">
        <div className="container-main">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight">Buy Offers (RFQs)</h1>
              <p className="text-slate-300 max-w-2xl">
                Browse real buying requirements from global businesses and respond with your best quotes
              </p>
            </div>
            <Link to="/rfqs/create">
              <Button className="mt-4 md:mt-0 bg-[#EA580C] hover:bg-[#C2410C]" data-testid="rfqs-create-btn">
                Post Buy Requirement
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <div className="container-main py-8">
        {/* Filters */}
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-8">
          <div className="flex flex-wrap items-center gap-4">
            <Select value={category || 'all'} onValueChange={(val) => updateFilter('category', val === 'all' ? '' : val)}>
              <SelectTrigger className="w-[180px]" data-testid="rfqs-category-filter">
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {categories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={country || 'all'} onValueChange={(val) => updateFilter('country', val === 'all' ? '' : val)}>
              <SelectTrigger className="w-[180px]" data-testid="rfqs-country-filter">
                <SelectValue placeholder="All Countries" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Countries</SelectItem>
                {countries.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={urgent || 'all'} onValueChange={(val) => updateFilter('urgent', val === 'all' ? '' : val)}>
              <SelectTrigger className="w-[180px]" data-testid="rfqs-urgent-filter">
                <SelectValue placeholder="All Requests" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Requests</SelectItem>
                <SelectItem value="true">Urgent Only</SelectItem>
              </SelectContent>
            </Select>

            {(category || country || urgent) && (
              <Button 
                variant="ghost" 
                onClick={() => setSearchParams({})}
              >
                Clear Filters
              </Button>
            )}

            <span className="ml-auto text-sm text-slate-600">
              {total} buy offers found
            </span>
          </div>
        </div>

        {/* RFQs List */}
        {loading ? (
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="bg-white border rounded-xl p-6 animate-pulse">
                <div className="h-4 bg-slate-200 rounded w-1/3 mb-4"></div>
                <div className="h-3 bg-slate-200 rounded w-full mb-2"></div>
                <div className="h-3 bg-slate-200 rounded w-2/3"></div>
              </div>
            ))}
          </div>
        ) : rfqs.length === 0 ? (
          <div className="text-center py-16">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-900 mb-2">No buy offers found</h3>
            <p className="text-slate-600">Try adjusting your filters</p>
          </div>
        ) : (
          <>
            <div className="space-y-4">
              {rfqs.map((rfq) => (
                <div
                  key={rfq.rfq_id}
                  className="bg-white border border-slate-200 hover:border-[#0A192F]/20 hover:shadow-md transition-all"
                  data-testid={`rfq-item-${rfq.rfq_id}`}
                >
                  <div className="p-6">
                    <div className="flex flex-col lg:flex-row lg:items-start gap-4">
                      <div className="flex-1">
                        <div className="flex items-start gap-3 mb-3">
                          <Link to={`/rfqs/${rfq.rfq_id}`}>
                            <h3 className="font-semibold text-lg text-[#0A192F] hover:text-[#EA580C] transition-colors">
                              {rfq.title}
                            </h3>
                          </Link>
                          {rfq.is_urgent && (
                            <Badge className="bg-red-50 text-red-700 border-red-200 flex-shrink-0">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Urgent
                            </Badge>
                          )}
                        </div>

                        <p className="text-slate-600 text-sm mb-4 line-clamp-2">{rfq.description}</p>

                        <div className="flex flex-wrap gap-4 text-sm">
                          <span className="flex items-center gap-1 text-slate-500">
                            <Package className="w-4 h-4" />
                            {rfq.quantity} {rfq.quantity_unit}
                          </span>
                          <span className="flex items-center gap-1 text-slate-500">
                            <MapPin className="w-4 h-4" />
                            Buyer from {rfq.destination_country || rfq.buyer_country}
                          </span>
                          {rfq.budget_max && (
                            <span className="flex items-center gap-1 text-slate-500">
                              <DollarSign className="w-4 h-4" />
                              ${rfq.budget_min?.toFixed(0)} - ${rfq.budget_max?.toFixed(0)} {rfq.budget_currency}
                            </span>
                          )}
                          <Badge variant="outline">{rfq.category}</Badge>
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <span className="flex items-center gap-1 text-xs text-slate-500">
                          <Clock className="w-3.5 h-3.5" />
                          {formatDate(rfq.created_at)}
                        </span>
                        <span className="text-xs text-slate-500">
                          {rfq.responses_count} quotes received
                        </span>
                        <Button
                          onClick={() => {
                            if (!isAuthenticated) { navigate('/login'); return; }
                            navigate(`/rfqs/${rfq.rfq_id}`);
                          }}
                          className="bg-[#0A192F] hover:bg-[#172A46] text-white rounded-sm h-9 px-5 text-xs font-semibold mt-1"
                          data-testid={`rfq-quote-btn-${rfq.rfq_id}`}
                        >
                          <Send className="w-3.5 h-3.5 mr-1.5" />
                          Send Quote
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-8">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => updateFilter('page', String(page - 1))}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-slate-600">
                  Page {page} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === totalPages}
                  onClick={() => updateFilter('page', String(page + 1))}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default RFQsPage;
